import { useMissionControlStore } from '../store.js';
import type { FileEntry } from '../types.js';

interface CodeSidecarProps {
  tree: FileEntry[];
  selectedPath: string | null;
}

const DEFAULT_TREE: FileEntry[] = [
  {
    path: 'packages/deck/src/missionControl/MissionControl.tsx',
    kind: 'file',
    changed: true,
    diff: '+ new shell\n+ real stores',
  },
  {
    path: 'packages/deck/src/missionControl/store.ts',
    kind: 'file',
    changed: true,
    diff: '+ zustand-backed multi-lane store',
  },
  {
    path: 'packages/deck/src/missionControl/swarm.ts',
    kind: 'file',
    changed: true,
    diff: '+ coordinator/scout/builder/reviewer',
  },
  { path: 'packages/server/src/runtime.ts', kind: 'file', changed: false },
  { path: 'packages/deck/src/lab', kind: 'dir', changed: false },
];

export function CodeSidecar({ tree, selectedPath }: CodeSidecarProps) {
  const setSelectedFile = useMissionControlStore((s) => s.setSelectedFile);
  const setFileTree = useMissionControlStore((s) => s.setFileTree);

  const entries = tree.length > 0 ? tree : DEFAULT_TREE;
  const selected = entries.find((entry) => entry.path === selectedPath) ?? null;

  return (
    <section className="panel code-sidecar" aria-label="Code sidecar">
      <div className="section-heading">
        <span className="section-kicker">Code sidecar</span>
        <strong>File tree + pending diffs</strong>
      </div>

      <div className="code-sidecar-body">
        <ul className="code-sidecar-tree">
          {entries.map((entry) => (
            <li key={entry.path} className={`code-entry code-entry-${entry.kind}`}>
              <button
                type="button"
                className={`code-entry-button ${entry.path === selectedPath ? 'code-entry-active' : ''} ${entry.changed ? 'code-entry-changed' : ''}`}
                onClick={() => setSelectedFile(entry.path)}
                aria-label={`Open ${entry.path}`}
              >
                <span className="code-entry-icon">
                  {entry.kind === 'dir' ? '▸' : entry.changed ? '●' : '·'}
                </span>
                <span className="code-entry-path">{entry.path}</span>
              </button>
            </li>
          ))}
        </ul>

        <div className="code-sidecar-diff">
          {selected ? (
            <>
              <div className="code-diff-head">
                <strong>{selected.path}</strong>
                <span>{selected.changed ? 'changes pending' : 'clean'}</span>
              </div>
              {selected.diff ? (
                <pre className="code-diff-body">{selected.diff}</pre>
              ) : (
                <p className="code-diff-empty">
                  No diff yet. When an agent writes, the diff will appear here.
                </p>
              )}
            </>
          ) : (
            <p className="code-diff-empty">Select a file from the tree to see pending changes.</p>
          )}
        </div>
      </div>

      {tree.length === 0 ? (
        <button type="button" className="ghost-button" onClick={() => setFileTree(DEFAULT_TREE)}>
          Load example tree
        </button>
      ) : null}
    </section>
  );
}
